/**
 * Break Monitoring Service (Dashboard side)
 * Shows driver break status, adjusts ETAs, alerts dispatch
 */

import { supabase } from '../supabaseClient';
import { parseDbTime } from '../utils/dbTime';

// __TZ_PICKER__ minutes since a DB timestamp; parseDbTime reads a naive value as UTC
// and leaves an offset-carrying one alone, so this is right whichever the column is.
const minsSince = (v) => { const d = parseDbTime(v); return d ? Math.floor((Date.now() - d.getTime()) / 60000) : 0; };

/**
 * Get all currently active breaks
 */
export const getActiveBreaks = async () => {
  const { data, error } = await supabase
    .from('driver_breaks')
    .select(`
      id,
      driver_id,
      status,
      expected_duration_minutes,
      started_at,
      movement_alerts_sent,
      start_latitude,
      start_longitude,
      driver:drivers(name, phone)
    `)
    .eq('status', 'active')
    .order('started_at', { ascending: true });

  if (error) {
    console.error('Error fetching active breaks:', error);
    return [];
  }

  return (data || []).map(b => ({
    ...b,
    driverName: b.driver?.name || 'Unknown',
    driverPhone: b.driver?.phone || '',
    elapsedMinutes: minsSince(b.started_at),
    isOvertime: minsSince(b.started_at) > b.expected_duration_minutes,
    hasMovementAlerts: b.movement_alerts_sent > 0,
  }));
};

/**
 * Subscribe to break changes in real-time
 * Fires on start, end, and movement alerts
 */
export const subscribeToBreaks = (onUpdate) => {
  const channel = supabase
    .channel('break_monitor')
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'driver_breaks',
    }, (payload) => {
      onUpdate(payload.eventType, payload.new, payload.old);
    })
    .subscribe();

  console.log('Subscribed to break updates');
  return () => supabase.removeChannel(channel);
};

/**
 * Calculate ETA adjustment for a driver on break
 * Returns minutes to add to all subsequent ETAs
 */
export const getETAAdjustment = (breakRecord) => {
  if (!breakRecord || breakRecord.status !== 'active') return 0;

  const elapsed = minsSince(breakRecord.started_at);
  const expected = breakRecord.expected_duration_minutes;

  // If still within expected break, use expected duration
  // If over, use actual elapsed (they're still on break)
  return Math.max(expected, elapsed);
};

/**
 * Format break status for display
 */
export const formatBreakStatus = (breakRecord) => {
  const elapsed = minsSince(breakRecord.started_at);
  const expected = breakRecord.expected_duration_minutes;
  const driverName = breakRecord.driverName || breakRecord.driver?.name || 'Driver';

  if (breakRecord.status !== 'active') {
    const endReason = {
      'manual': 'resumed driving',
      'auto_timeout': 'auto-ended (60 min limit)',
      'movement_detected': 'auto-ended (movement)',
      'no_confirmation': 'auto-ended (no response)',
      'new_break_started': 'restarted break',
    }[breakRecord.end_reason] || breakRecord.end_reason;

    return {
      icon: '✅',
      title: `${driverName} — break ended`,
      detail: endReason,
      priority: 'resolved',
    };
  }

  if (breakRecord.hasMovementAlerts || breakRecord.movement_alerts_sent > 0) {
    return {
      icon: '⚠️',
      title: `${driverName} — MOVING during break`,
      detail: `${elapsed} min elapsed, ${breakRecord.movement_alerts_sent} movement alert(s)`,
      priority: 'warning',
    };
  }

  if (elapsed > expected) {
    return {
      icon: '🟡',
      title: `${driverName} — break running long`,
      detail: `${elapsed} min (expected ${expected} min)`,
      priority: 'attention',
    };
  }

  return {
    icon: '☕',
    title: `${driverName} — on break`,
    detail: `${elapsed}/${expected} min`,
    priority: 'info',
  };
};

/**
 * Force-end a driver's break (dispatch action)
 */
export const forceEndBreak = async (driverId) => {
  const { error } = await supabase.rpc('end_driver_break', {
    p_driver_id: driverId,
    p_reason: 'dispatch_ended',
  });

  return !error;
};

export default {
  getActiveBreaks,
  subscribeToBreaks,
  getETAAdjustment,
  formatBreakStatus,
  forceEndBreak,
};
